import React from 'react';
import { Link } from 'react-router-dom';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';


export default function AboutPage() {
  const { lang } = useSportsLang();
  const LABELS = {
    pt: {
      heading: 'Sobre a Arena Stats',
      subtitle: 'Sua plataforma completa de estatísticas esportivas',
      missionTitle: 'Nossa Missão',
      missionText: 'A Arena Stats é uma plataforma dedicada a fornecer estatísticas esportivas detalhadas, análises profundas e insights sobre diversos esportes. Nossa missão é democratizar o acesso a dados esportivos de qualidade, permitindo que fãs, atletas, treinadores e pesquisadores tenham acesso a informações precisas e atualizadas.',
      whatTitle: 'O que Fazemos',
      collectTitle: 'Coleta e Organização de Dados',
      collectDesc: 'Agregamos dados de múltiplas fontes confiáveis para criar uma base completa de estatísticas esportivas.',
      analysisTitle: 'Análises Avançadas',
      analysisDesc: 'Transformamos dados brutos em insights valiosos através de análises estatísticas e visualizações intuitivas.',
      researchTitle: 'Pesquisa e Desenvolvimento',
      researchDesc: 'Utilizamos dados anonimizados para estudos acadêmicos e desenvolvimento de novos recursos, sempre respeitando a privacidade dos usuários conforme a LGPD.',
      dataUsageTitle: 'Uso de Dados para Estudos e Pesquisa',
      dataUsageIntro: 'A Arena Stats utiliza dados coletados no cadastro (como preferências esportivas, esportes praticados, times favoritos, etc.) exclusivamente para fins de:',
      dataItems: [
        { bold: 'Estudos estatísticos:', rest: ' Análise de tendências e comportamento do público esportivo' },
        { bold: 'Pesquisa acadêmica:', rest: ' Desenvolvimento de trabalhos científicos sobre esportes e engajamento' },
        { bold: 'Melhoria da plataforma:', rest: ' Personalização de conteúdo e desenvolvimento de novos recursos' },
      ],
      lgpdTitle: 'Compromisso com a LGPD:',
      lgpdItems: [
        'Todos os dados são anonimizados antes do uso em estudos',
        'Nunca compartilhamos dados pessoais identificáveis com terceiros',
        'Você pode solicitar exclusão de seus dados a qualquer momento',
        'Os dados são utilizados apenas com seu consentimento explícito',
        'Total transparência sobre como seus dados são utilizados',
      ],
      privacyPre: 'Para mais informações, consulte nossa',
      privacyLink: 'Política de Privacidade',
      techTitle: 'Tecnologia e Segurança',
      techText: 'Utilizamos tecnologias modernas e práticas de segurança robustas para garantir a integridade, disponibilidade e confidencialidade dos dados:',
      techItems: [
        'Criptografia de dados em trânsito (HTTPS/TLS)',
        'Armazenamento seguro em banco de dados Azure SQL',
        'Autenticação e autorização por tokens JWT',
        'Backups regulares e redundância de dados',
        'Conformidade com LGPD e boas práticas de privacidade',
      ],
      contactTitle: 'Entre em Contato',
      contactText: 'Tem dúvidas, sugestões ou quer saber mais sobre como utilizamos os dados? Estamos aqui para ajudar!',
      contactBtn: 'Fale Conosco',
    },
    en: {
      heading: 'About Arena Stats',
      subtitle: 'Your complete sports statistics platform',
      missionTitle: 'Our Mission',
      missionText: 'Arena Stats is a platform dedicated to providing detailed sports statistics, in-depth analysis, and insights about various sports. Our mission is to democratize access to quality sports data, allowing fans, athletes, coaches, and researchers to have access to accurate and up-to-date information.',
      whatTitle: 'What We Do',
      collectTitle: 'Data Collection & Organization',
      collectDesc: 'We aggregate data from multiple reliable sources to create a comprehensive sports statistics database.',
      analysisTitle: 'Advanced Analysis',
      analysisDesc: 'We transform raw data into valuable insights through statistical analysis and intuitive visualizations.',
      researchTitle: 'Research & Development',
      researchDesc: 'We use anonymized data for academic studies and new feature development, always respecting user privacy in accordance with LGPD.',
      dataUsageTitle: 'Use of Data for Studies and Research',
      dataUsageIntro: 'Arena Stats uses data collected during registration (such as sports preferences, sports practiced, favorite teams, etc.) exclusively for:',
      dataItems: [
        { bold: 'Statistical studies:', rest: ' Analysis of trends and sports audience behavior' },
        { bold: 'Academic research:', rest: ' Development of scientific works on sports and engagement' },
        { bold: 'Platform improvement:', rest: ' Content personalization and new feature development' },
      ],
      lgpdTitle: 'LGPD Commitment:',
      lgpdItems: [
        'All data is anonymized before use in studies',
        'We never share personally identifiable data with third parties',
        'You can request deletion of your data at any time',
        'Data is used only with your explicit consent',
        'Full transparency about how your data is used',
      ],
      privacyPre: 'For more information, see our',
      privacyLink: 'Privacy Policy',
      techTitle: 'Technology & Security',
      techText: 'We use modern technologies and robust security practices to ensure data integrity, availability, and confidentiality:',
      techItems: [
        'Data encryption in transit (HTTPS/TLS)',
        'Secure storage in Azure SQL database',
        'Authentication and authorization via JWT tokens',
        'Regular backups and data redundancy',
        'LGPD compliance and privacy best practices',
      ],
      contactTitle: 'Contact Us',
      contactText: "Have questions, suggestions, or want to learn more about how we use data? We're here to help!",
      contactBtn: 'Get in Touch',
    },
  };
  const T = LABELS[lang] || LABELS.pt;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-4" style={{ color: BRAND_GREEN }}>
          {T.heading}
        </h1>
        <p className="text-lg text-gray-600">
          {T.subtitle}
        </p>
      </div>

      <div className="space-y-8">
        {/* Missão */}
        <section className="bg-white rounded-xl border-2 p-6" style={{ borderColor: 'var(--brand-border)' }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: BRAND_GREEN }}>
            {T.missionTitle}
          </h2>
          <p className="text-gray-700 leading-relaxed">
            {T.missionText}
          </p>
        </section>

        {/* O que fazemos */}
        <section className="bg-white rounded-xl border-2 p-6" style={{ borderColor: 'var(--brand-border)' }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: BRAND_GREEN }}>
            {T.whatTitle}
          </h2>
          <div className="space-y-4">
            {[
              { title: T.collectTitle, desc: T.collectDesc },
              { title: T.analysisTitle, desc: T.analysisDesc },
              { title: T.researchTitle, desc: T.researchDesc },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: BRAND_GOLD }}>
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Uso de Dados */}
        <section className="bg-green-50 rounded-xl border-2 p-6" style={{ borderColor: BRAND_GREEN }}>
          <div className="flex items-start gap-3 mb-4">
            <svg className="w-6 h-6 flex-shrink-0" style={{ color: BRAND_GREEN }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h2 className="text-2xl font-bold mb-3" style={{ color: BRAND_GREEN }}>
                {T.dataUsageTitle}
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {T.dataUsageIntro}
              </p>
              <ul className="space-y-2 mb-4">
                {T.dataItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">•</span>
                    <span className="text-gray-700">
                      <strong>{item.bold}</strong>{item.rest}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="bg-white rounded-lg p-4 border-2" style={{ borderColor: BRAND_GREEN }}>
                <p className="text-sm text-gray-700 mb-2">
                  <strong style={{ color: BRAND_GREEN }}>{T.lgpdTitle}</strong>
                </p>
                <ul className="space-y-1 text-sm text-gray-600">
                  {T.lgpdItems.map((item, i) => (
                    <li key={i}>✓ {item}</li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                {T.privacyPre}{' '}
                <Link to="/privacidade" className="font-semibold hover:underline" style={{ color: BRAND_GREEN }}>
                  {T.privacyLink}
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Tecnologia */}
        <section className="bg-white rounded-xl border-2 p-6" style={{ borderColor: 'var(--brand-border)' }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: BRAND_GREEN }}>
            {T.techTitle}
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            {T.techText}
          </p>
          <ul className="space-y-2 text-gray-700">
            {T.techItems.map((item, i) => (
              <li key={i}>• {item}</li>
            ))}
          </ul>
        </section>

        {/* Contato */}
        <section className="bg-white rounded-xl border-2 p-6" style={{ borderColor: 'var(--brand-border)' }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: BRAND_GREEN }}>
            {T.contactTitle}
          </h2>
          <p className="text-gray-700 mb-4">
            {T.contactText}
          </p>
          <Link
            to="/contato"
            className="inline-block px-6 py-3 rounded-lg font-semibold text-white transition-all hover:shadow-lg"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            {T.contactBtn}
          </Link>
        </section>
      </div>
    </div>
  );
}
